import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { MaterialModule } from '../../../material/material.module';
import { PlayerTagViewModel } from '../../../shared/models/player.model';
import { PlayerService } from '../../../shared/services/player.service';

@Component({
  selector: 'app-tags',
  imports: [CommonModule, MaterialModule, ReactiveFormsModule],
  templateUrl: './tags.component.html',
  styleUrl: './tags.component.css'
})
export class TagsComponent implements OnInit {
  displayedColumns: string[] = ['TagText', 'Users', 'Actions'];
  dataSource = new MatTableDataSource<PlayerTagViewModel>([]);
  isLoading = false;
  editorMode: 'NEW' | 'EDIT' = 'NEW';
  isEditorOpen = false;
  tagForm: UntypedFormGroup;
  private editingTag: PlayerTagViewModel | null = null;

  constructor(
    private playerService: PlayerService,
    private formBuilder: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private location: Location
  ) {
    this.tagForm = this.formBuilder.group({
      TagId: [''],
      TextEnglish: ['', [Validators.required, Validators.maxLength(100)]],
      TextSwedish: ['', [Validators.required, Validators.maxLength(100)]],
      TextFinnish: ['', [Validators.required, Validators.maxLength(100)]],
      TagColor: ['sc-playertag-green', [Validators.required]],
      Active: [true]
    });
  }

  ngOnInit(): void {
    this.loadTags();
  }

  back(): void {
    this.location.back();
  }

  loadTags(): void {
    this.isLoading = true;
    this.playerService.getTags().subscribe({
      next: (tags) => {
        this.dataSource.data = (tags as PlayerTagViewModel[]) || [];
        this.isLoading = false;
      },
      error: () => {
        this.dataSource.data = [];
        this.isLoading = false;
      }
    });
  }

  openCreate(): void {
    this.editorMode = 'NEW';
    this.editingTag = null;
    this.tagForm.reset({
      TagId: '',
      TextEnglish: '',
      TextSwedish: '',
      TextFinnish: '',
      TagColor: 'sc-playertag-green',
      Active: true
    });
    this.isEditorOpen = true;
  }

  openEdit(tag: PlayerTagViewModel): void {
    this.editorMode = 'EDIT';
    this.editingTag = tag;
    this.tagForm.patchValue({
      TagId: tag.TagId,
      TextEnglish: this.getLanguageText(tag, 'en-US') || tag.TagText,
      TextSwedish: this.getLanguageText(tag, 'sv-SE') || tag.TagText,
      TextFinnish: this.getLanguageText(tag, 'fi-FI') || tag.TagText,
      TagColor: tag.TagColor || 'sc-playertag-green',
      Active: (tag as any).Active ?? true
    });
    this.isEditorOpen = true;
  }

  cancelEdit(): void {
    this.isEditorOpen = false;
    this.editingTag = null;
  }

  save(): void {
    if (this.tagForm.invalid) {
      return;
    }

    const formValue = this.tagForm.value;
    const payload: any = {
      TagId: formValue.TagId,
      TagText: formValue.TextEnglish,
      TagColor: formValue.TagColor,
      Active: formValue.Active,
      PlayerTagsSecurity: this.editingTag?.PlayerTagsSecurity || [],
      TextEnglish: formValue.TextEnglish,
      TextSwedish: formValue.TextSwedish,
      TextFinnish: formValue.TextFinnish,
      PlayerTagsLanguage: [
        { TagId: formValue.TagId, LanguageId: 'sv-SE', Text: formValue.TextSwedish },
        { TagId: formValue.TagId, LanguageId: 'S', Text: formValue.TextSwedish },
        { TagId: formValue.TagId, LanguageId: 'en-US', Text: formValue.TextEnglish },
        { TagId: formValue.TagId, LanguageId: 'E', Text: formValue.TextEnglish },
        { TagId: formValue.TagId, LanguageId: 'fi-FI', Text: formValue.TextFinnish }
      ]
    };

    const request = this.editorMode === 'NEW'
      ? this.playerService.createTag(payload)
      : this.playerService.saveTag(payload);

    request.subscribe({
      next: () => {
        this.snackBar.open(this.editorMode === 'NEW' ? 'Tag created' : 'Tag updated', 'CLOSE', { duration: 2000 });
        this.isEditorOpen = false;
        this.editingTag = null;
        this.loadTags();
      },
      error: () => {
        this.snackBar.open('Failed to save tag', 'CLOSE', { duration: 2500 });
      }
    });
  }

  delete(tag: PlayerTagViewModel): void {
    if (!tag?.TagId) {
      return;
    }

    this.playerService.deleteTag(tag.TagId).subscribe({
      next: () => {
        this.snackBar.open('Tag deleted', 'CLOSE', { duration: 2000 });
        this.loadTags();
      },
      error: () => {
        this.snackBar.open('Failed to delete tag', 'CLOSE', { duration: 2500 });
      }
    });
  }

  private getLanguageText(tag: any, languageId: string): string {
    return tag?.PlayerTagsLanguage?.find((l: any) => l.LanguageId === languageId)?.Text || '';
  }
}
